import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { ProductService } from '../api/api_pb';
import { useClient } from '../connect_api';
import { S3Service } from '../api/api_pb';
import { useAuth } from '../AuthContext';

interface CreateProductModalProps {
    isOpen: boolean;
    onClose: () => void;
    onProductCreated: () => void;
}

const CreateProductModal: React.FC<CreateProductModalProps> = ({ isOpen, onClose, onProductCreated }) => {
    const s3Client = useClient(S3Service);
    const productClient = useClient(ProductService);
    const { organizationInfo } = useAuth();
    const [formData, setFormData] = useState({
        name: '',
        imageFile: null as File | null,
        imageUrl: '',
        price: '', 
        leaseDurationLimit: '',
        forSale: false
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        const decimalPlaces = organizationInfo?.currencyDecimalPoint ?? 2;
        if (name === "price") {
            let formattedValue = value.replace(/[^0-9.]/g, ""); 
            const parts = formattedValue.split(".");
            if (parts.length > decimalPlaces) {
                formattedValue = parts[0] + "." + parts.slice(1).join("");
            }
            if (parts.length === decimalPlaces && parts[1].length > decimalPlaces) {
                formattedValue = parts[0] + "." + parts[1].slice(0, decimalPlaces);
            }
    
            setFormData((prev) => ({
                ...prev,
                price: formattedValue,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: type === "checkbox" ? checked : value,
            }));
        }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setFormData((prev) => ({
                ...prev,
                imageFile: file,
                imageUrl: URL.createObjectURL(file)
            }));
        }
    };

    const resetFormData = () => {
        setFormData({
            name: '',
            imageFile: null,
            imageUrl: '',
            price: '',
            leaseDurationLimit: '',
            forSale: false
        });
    };

    const handleCreate = async () => {
        if (!formData.name || !formData.imageFile || !formData.price || !formData.leaseDurationLimit) {
            toast.error("All fields are required!");
            return;
        }

        try {
            const decimalPlaces = organizationInfo?.currencyDecimalPoint ?? 2;
            const divisor = Math.pow(10, decimalPlaces);
            const priceInCents = Math.round(parseFloat(formData.price) * divisor);
            
            let imageUrl = '';
            if (formData.imageFile) {
                const extension = formData.imageFile.name.split('.').pop() || '';
                const presignedUrlResponse = await s3Client.getUploadUrl({ extension });
                const presignedUrl = presignedUrlResponse.url;

                const uploadResponse = await fetch(presignedUrl, {
                    method: 'PUT',
                    body: formData.imageFile,
                    headers: { 'Content-Type': formData.imageFile.type },
                });

                if (!uploadResponse.ok) throw new Error('Failed to upload image');

                imageUrl = presignedUrl.split('?')[0];
            }

            await productClient.createProduct({
                name: formData.name,
                imageUrl,
                priceInCents,
                leaseDurationLimit: parseInt(formData.leaseDurationLimit, 10),
                forSale: formData.forSale,
            });

            toast.success('Product created successfully!');
            onClose();
            onProductCreated(); 
            resetFormData();
        } catch (error) {
            toast.error('Failed to create product: ' + (error as Error).message);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-96">
                <h2 className="text-xl font-bold mb-4">Create Product</h2>

                <label className="block text-sm font-medium">Name</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="border p-2 w-full mb-2" placeholder="Enter product name..."/>

                <label className="block text-sm font-medium">Image</label>
                <input type="file" accept="image/*" onChange={handleImageChange} className="mb-2" />
                {formData.imageUrl && <img src={formData.imageUrl} alt="Preview" className="w-24 h-24 mb-2 object-contain" />}

                <label className="block text-sm font-medium">Price</label>
                <div className="flex items-center border p-2 w-full mb-2">
                    <span className="mr-2">{organizationInfo?.currencySymbol ?? '$'}</span>
                    <input
                        type="text" 
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        className="flex-1 outline-none"
                        placeholder={`Enter price (e.g., 12.02)`}
                    />
                </div>
                <label className="block text-sm font-medium">Lease Duration Limit (seconds)</label>
                <input type="number" name="leaseDurationLimit" value={formData.leaseDurationLimit} onChange={handleInputChange} className="border p-2 w-full mb-2" placeholder="Enter lease duration in seconds..."/>

                <label className="flex items-center space-x-2">
                    <input type="checkbox" name="forSale" checked={formData.forSale} onChange={handleInputChange} />
                    <span>For Sale</span>
                </label>

                <div className="mt-4 flex justify-end">
                    <button onClick={onClose} className="bg-gray-300 px-4 py-2 rounded-md">Cancel</button>
                    <button onClick={handleCreate} className="bg-blue-600 text-white px-4 py-2 rounded-md ml-2">Create</button>
                </div>
            </div>
        </div>
    );
};

export default CreateProductModal;
